import {
  Alert,
  Button,
  Input,
  InputOtp,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Radio,
  RadioGroup,
} from '@heroui/react';
import _ from 'lodash';
import { ChangeEvent, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { useAppSelector } from '@/redux/hooks';
import {
  useSendTwoFACodeMutation,
  useSetupTwoFAMutation,
} from '@/patient-app-common/api/authApi';
import { useUpdatePatientMutation } from '@/patient-app-common/api/patientApi';
import { isPhone } from '@/patient-app-common/utils';

interface SetTwoFAProps {
  handleExit: () => void;
  mfaType: 'required' | 'optiona' | null;
}
export function SetTwoFA({ handleExit, mfaType }: SetTwoFAProps) {
  const { t } = useTranslation('settings');

  const { mfaContact } = useAppSelector(({ auth }) => auth);

  const [updatePatient, { isLoading }] = useUpdatePatientMutation();
  const [sendTwoFACode, { isLoading: isLoadingSend }] =
    useSendTwoFACodeMutation();
  const [setupTwoFA, { isLoading: isLoadingSetup }] = useSetupTwoFAMutation();
  const [timer, setTimer] = useState<number | null>(30);

  const [showEdit, setShowEdit] = useState(!_.isEmpty(mfaContact));
  const [page, setPage] = useState(1);

  const [form, setForm] = useState<{ mfaContact: string; method: string }>({
    mfaContact: '',
    method: 'sms',
  });

  const [code, setCode] = useState('');
  const [error, setError] = useState('');

  useEffect(() => {
    if (timer === 0) {
      setTimer(null);
    }

    if (!timer) return;

    const timerId = setInterval(() => {
      setTimer(timer - 1);
    }, 1000);

    return () => clearInterval(timerId);
  }, [timer]);

  const disable2FA = async () => {
    try {
      const result = await updatePatient({
        mfaChannel: null,
        mfaContact: null,
      }).unwrap();

      if (result.success) {
        handleExit();
      } else {
        setError(t('common.error.not_saved'));
      }
    } catch (e) {
      console.error(e);
      setError(t('common.error.not_saved'));
    }
  };

  const handleInputChange = (value: string, name: string) => {
    setError('');

    setForm((prevState) => ({
      ...prevState,
      [name]: value,
    }));
  };

  const validateContact = (form: { mfaContact: string; method: string }) => {
    const { mfaContact } = form;

    if (!mfaContact) {
      setError(t('common.error.blank_input'));
      return false;
    }

    const validPhone = isPhone(mfaContact);
    let errors = '';

    if (!validPhone) {
      errors = `${t('common.error.invalid_phone')}\n`;
    }

    setError(errors);
    return validPhone;
  };

  const sendCode = async () => {
    setTimer(30);

    try {
      const request = await sendTwoFACode(form).unwrap();

      if (!request.success) {
        setError(t('settwofa.error.send_code_failed'));
      }

      return request.success;
    } catch (e) {
      console.error(e);
      setError(t('settwofa.error.send_code_failed'));
      return false;
    }
  };

  const startValidation = async () => {
    const isValid = validateContact(form);

    if (isValid) {
      const result = await sendCode();

      if (result) {
        setPage(2);
      }
    }
  };

  const checkVerification = async () => {
    if (!code || code.length < 6) {
      setError(t('common.error.blank_input'));
      return;
    }

    try {
      const result = await setupTwoFA({
        ...form,
        code,
      }).unwrap();

      if (result.success) {
        handleExit();
      } else {
        setError(t('settwofa.error.verification_failed'));
      }
    } catch (e) {
      console.error(e);
      setError(t('settwofa.error.verification_failed'));
    }
  };

  const btnText = useMemo(() => {
    let text = t('common.two_fa.button_resend');
    if (timer) {
      text += ` (${timer}s)`;
    }
    return text;
  }, [timer]);

  if (showEdit) {
    return (
      <>
        <ModalHeader>
          <h2>{t('settwofa.title_edit')}</h2>
        </ModalHeader>
        <ModalBody>
          <Alert
            color="danger"
            description={error}
            hideIconWrapper
            isVisible={!_.isEmpty(error)}
            variant="faded"
          />
          <p className="py-4">
            {t('settwofa.description_edit', { number: mfaContact })}
            {mfaType !== 'required'
              ? ` ${t('settwofa.text_disable_true')}`
              : ` ${t('settwofa.text_disable_false')}`}
          </p>
        </ModalBody>
        <ModalFooter>
          <Button color="primary" onPress={() => setShowEdit(false)}>
            {t('settwofa.button.edit')}
          </Button>
          {mfaType !== 'required' && (
            <Button
              color="primary"
              isLoading={isLoading}
              onPress={disable2FA}
              variant="bordered"
            >
              {t('settwofa.button.disable')}
            </Button>
          )}
        </ModalFooter>
      </>
    );
  }

  return (
    <>
      {page === 1 && (
        <>
          <ModalHeader>
            <h2>{t('settwofa.page_one.title')}</h2>
          </ModalHeader>
          <ModalBody className="gap-4">
            <Alert
              color="danger"
              description={error}
              hideIconWrapper
              isVisible={!_.isEmpty(error)}
              variant="faded"
            />
            <p>{t('settwofa.page_one.description')}</p>
            <Input
              autoComplete="off"
              color="primary"
              isInvalid={!_.isEmpty(error)}
              label={t('common.text.phone')}
              onChange={(e: ChangeEvent<HTMLInputElement>) =>
                handleInputChange(e.target.value, 'mfaContact')
              }
              variant="bordered"
            />
            <RadioGroup
              onChange={(e: ChangeEvent<HTMLInputElement>) =>
                handleInputChange(e.target.value, 'method')
              }
              orientation="horizontal"
            >
              <Radio value="sms">{t('common.two_fa.radio_sms')}</Radio>
              <Radio value="call">{t('common.two_fa.radio_call')}</Radio>
            </RadioGroup>
          </ModalBody>
          <ModalFooter>
            <Button
              color="primary"
              isLoading={isLoadingSend}
              onPress={startValidation}
              variant="bordered"
            >
              {t('common.action.next')}
            </Button>
          </ModalFooter>
        </>
      )}

      {page === 2 && (
        <>
          <ModalHeader>
            <h2>{t('settwofa.page_two.title')}</h2>
          </ModalHeader>
          <ModalBody className="gap-4">
            <Alert
              color="danger"
              description={error}
              hideIconWrapper
              isVisible={!_.isEmpty(error)}
              variant="faded"
            />
            <p>
              {t('settwofa.page_two.description', {
                number: form.mfaContact.slice(-4),
              })}
            </p>
            <InputOtp
              color="primary"
              className="mx-auto"
              errorMessage={' '}
              onChange={(e: ChangeEvent<HTMLInputElement>) =>
                setCode(e.target.value)
              }
              label={t('common.two_fa.input_code')}
              length={6}
              variant="faded"
            />
            <p>{t('common.two_fa.change_method')}</p>
            <div className="inline-flex items-center gap-8">
              <RadioGroup
                onChange={(e: ChangeEvent<HTMLInputElement>) =>
                  handleInputChange(e.target.value, 'method')
                }
                orientation="horizontal"
              >
                <Radio value="sms">{t('common.two_fa.radio_sms')}</Radio>
                <Radio value="call">{t('common.two_fa.radio_call')}</Radio>
              </RadioGroup>
              <Button
                color="primary"
                isDisabled={_.isNumber(timer)}
                onPress={sendCode}
                size="sm"
                variant="flat"
              >
                {btnText}
              </Button>
            </div>
          </ModalBody>
          <ModalFooter>
            <Button
              color="primary"
              isLoading={isLoadingSetup}
              onPress={checkVerification}
            >
              {t('common.action.submit')}
            </Button>
          </ModalFooter>
        </>
      )}
    </>
  );
}
